import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import * as api from '../services/api';
import { useAuth } from '../context/AuthContext';

const emptyRegisterForm = {
  schoolName: '',
  shortName: '',
  schoolEmail: '',
  schoolPhone: '',
  street: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  firstName: '',
  middleName: '',
  lastName: '',
  username: '',
  email: '',
  password: '',
  phone: '',
  age: '30',
  gender: 'male' as 'male' | 'female',
  cnic: ''
};

export function RegisterSchoolPage() {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [registerForm, setRegisterForm] = useState(emptyRegisterForm);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  if (!isSuperAdmin) {
    return <Navigate to="/schools" replace />;
  }

  function updateRegisterField<K extends keyof typeof emptyRegisterForm>(
    key: K,
    value: (typeof emptyRegisterForm)[K]
  ) {
    setRegisterForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleRegister(event: FormEvent) {
    event.preventDefault();
    setRegistering(true);
    setError(null);
    setSuccess(null);
    try {
      const age = Number(registerForm.age);
      if (!Number.isFinite(age) || age < 18) {
        throw new Error('School admin age must be at least 18');
      }

      const result = await api.registerSchool({
        firstName: registerForm.firstName.trim(),
        middleName: registerForm.middleName.trim() || undefined,
        lastName: registerForm.lastName.trim(),
        username: registerForm.username.trim(),
        email: registerForm.email.trim(),
        password: registerForm.password,
        phone: registerForm.phone.trim() || undefined,
        age,
        gender: registerForm.gender,
        cnic: registerForm.cnic.trim(),
        school: {
          name: registerForm.schoolName.trim(),
          shortName: registerForm.shortName.trim() || undefined,
          address: {
            street: registerForm.street.trim() || undefined,
            city: registerForm.city.trim() || undefined,
            state: registerForm.state.trim() || undefined,
            postalCode: registerForm.postalCode.trim() || undefined,
            country: registerForm.country.trim() || undefined
          },
          contactInfo: {
            email: registerForm.schoolEmail.trim() || undefined,
            phone: registerForm.schoolPhone.trim() || undefined
          }
        }
      });

      setRegisterForm(emptyRegisterForm);
      setSuccess(
        `Registered ${result.school.name}. School and admin are pending until you approve the registration.`
      );
      navigate('/schools?tab=pending', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to register school');
    } finally {
      setRegistering(false);
    }
  }

  return (
    <section className="admin-panel schools-page">
      <div className="panel-heading">
        <div>
          <h2>Register school</h2>
          <p>
            Creates a pending school and a pending SCHOOL_ADMIN account. Approve the registration
            afterward to activate both.
          </p>
        </div>
        <Link to="/schools" className="ghost-btn">
          Back to schools
        </Link>
      </div>

      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="form-success" role="status">
          {success}
        </p>
      ) : null}

      <form className="settings-section register-school-form" onSubmit={handleRegister}>
        <div className="settings-section-body">
          <fieldset className="form-fieldset">
            <legend>School details</legend>
            <div className="form-grid">
              <label>
                School name
                <input
                  required
                  value={registerForm.schoolName}
                  onChange={(e) => updateRegisterField('schoolName', e.target.value)}
                />
              </label>
              <label>
                Short name
                <input
                  value={registerForm.shortName}
                  onChange={(e) => updateRegisterField('shortName', e.target.value)}
                  placeholder="Optional"
                />
              </label>
              <label>
                School email
                <input
                  type="email"
                  value={registerForm.schoolEmail}
                  onChange={(e) => updateRegisterField('schoolEmail', e.target.value)}
                />
              </label>
              <label>
                School phone
                <input
                  value={registerForm.schoolPhone}
                  onChange={(e) => updateRegisterField('schoolPhone', e.target.value)}
                />
              </label>
              <label>
                Street
                <input
                  value={registerForm.street}
                  onChange={(e) => updateRegisterField('street', e.target.value)}
                />
              </label>
              <label>
                City
                <input
                  value={registerForm.city}
                  onChange={(e) => updateRegisterField('city', e.target.value)}
                />
              </label>
              <label>
                State
                <input
                  value={registerForm.state}
                  onChange={(e) => updateRegisterField('state', e.target.value)}
                />
              </label>
              <label>
                Postal code
                <input
                  value={registerForm.postalCode}
                  onChange={(e) => updateRegisterField('postalCode', e.target.value)}
                />
              </label>
              <label>
                Country
                <input
                  value={registerForm.country}
                  onChange={(e) => updateRegisterField('country', e.target.value)}
                />
              </label>
            </div>
          </fieldset>

          <fieldset className="form-fieldset">
            <legend>School admin account</legend>
            <div className="form-grid">
              <label>
                First name
                <input
                  required
                  value={registerForm.firstName}
                  onChange={(e) => updateRegisterField('firstName', e.target.value)}
                />
              </label>
              <label>
                Middle name
                <input
                  value={registerForm.middleName}
                  onChange={(e) => updateRegisterField('middleName', e.target.value)}
                />
              </label>
              <label>
                Last name
                <input
                  required
                  value={registerForm.lastName}
                  onChange={(e) => updateRegisterField('lastName', e.target.value)}
                />
              </label>
              <label>
                Username
                <input
                  required
                  autoComplete="off"
                  value={registerForm.username}
                  onChange={(e) => updateRegisterField('username', e.target.value)}
                />
              </label>
              <label>
                Email
                <input
                  required
                  type="email"
                  autoComplete="off"
                  value={registerForm.email}
                  onChange={(e) => updateRegisterField('email', e.target.value)}
                />
              </label>
              <label>
                Password
                <input
                  required
                  type="password"
                  autoComplete="new-password"
                  minLength={6}
                  value={registerForm.password}
                  onChange={(e) => updateRegisterField('password', e.target.value)}
                />
              </label>
              <label>
                Phone
                <input
                  value={registerForm.phone}
                  onChange={(e) => updateRegisterField('phone', e.target.value)}
                />
              </label>
              <label>
                Age
                <input
                  required
                  type="number"
                  min={18}
                  max={120}
                  value={registerForm.age}
                  onChange={(e) => updateRegisterField('age', e.target.value)}
                />
              </label>
              <label>
                Gender
                <select
                  value={registerForm.gender}
                  onChange={(e) =>
                    updateRegisterField('gender', e.target.value as 'male' | 'female')
                  }
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </label>
              <label>
                CNIC (13 digits)
                <input
                  required
                  value={registerForm.cnic}
                  onChange={(e) => updateRegisterField('cnic', e.target.value)}
                  placeholder="12345-1234567-1"
                />
              </label>
            </div>
          </fieldset>

          <div className="page-form-actions">
            <button type="submit" className="primary-btn" disabled={registering}>
              {registering ? 'Registering…' : 'Register school'}
            </button>
            <Link to="/schools" className="ghost-btn">
              Cancel
            </Link>
          </div>
        </div>
      </form>
    </section>
  );
}
